"use client"

import { ArrowLeft, ArrowRight, Focus, Images, Loader2, Lock, Plus, Sun, SunDim, X } from "lucide-react"

import { cn } from "@/lib/utils"
import type { AnalysisMode as SharedAnalysisMode } from "@/lib/types/diagnosis"
import { AnalysisLawnChoice } from "@/components/home/analysis-lawn-choice"
import { PhotoQualityWarning } from "@/components/home/photo-quality-warning"
import type { PhotoQualityReason } from "@/lib/analysis/photo-quality"
import type { PhotoQualityWarning as PhotoQualityWarningValue, SupportingAnalysisImage } from "@/hooks/use-homepage-analysis"
import type { UserLawn } from "@/hooks/use-default-lawn"

type AnalysisMode = Exclude<SharedAnalysisMode, 'weed'>

const SUPPORTING_QUALITY_ICON = {
  blur: Focus,
  dark: Sun,
  bright: SunDim,
} as const

const SUPPORTING_QUALITY_LABEL: Record<PhotoQualityReason, string> = {
  blur: "Looks blurry",
  dark: "Looks dark",
  bright: "Looks washed out",
}

/**
 * The photo step shown before a run that is not automatic: today, the
 * post-result refinement lane where the user adds a supporting view. There is
 * no location input; the server infers location from the request.
 */
interface SupportingPhotoStepProps {
  uploadedImage: string
  onAnalyze: () => void
  onReset?: () => void
  mode?: AnalysisMode
  supportingImages?: SupportingAnalysisImage[]
  isUploadingSupportingImages?: boolean
  supportingImageError?: string
  onSupportingImagesUpload?: (files: File[]) => void
  onRemoveSupportingImage?: (url: string) => void
  /** Post-result refinement lane. Supporting photos are only ever collected
   *  here; the first analysis is single-photo for every tier. */
  isRefinementStep?: boolean
  requireSupportingPhoto?: boolean
  supportingPhotoGuidance?: string | null
  /** Anonymous visitor: the slots render, but the picker is replaced by a
   *  sign-in affordance. The server rejects a multi-photo anonymous request,
   *  so an open picker here would only produce an error later. */
  supportingPhotosLocked?: boolean
  onSupportingPhotosSignIn?: () => void
  /** Total photos this visitor may send, primary included. */
  photoCap?: number
  lawns?: UserLawn[]
  lawnChoice?: string | null
  lawnChoiceRequired?: boolean
  lawnChoiceMade?: boolean
  onLawnChoice?: (lawnId: string | null) => void
  /** Pre-call capture warning for the primary photo. Advisory: it never gates
   *  the analyze button. */
  photoQuality?: PhotoQualityWarningValue | null
  /** Supporting-photo warnings, keyed by uploaded URL. */
  supportingPhotoQuality?: Record<string, PhotoQualityReason>
  onQualityRetake?: () => void
  funnelId?: string | null
  authState?: 'anonymous' | 'authenticated' | 'unknown'
  className?: string
}

export function SupportingPhotoStep({
  uploadedImage,
  onAnalyze,
  onReset,
  mode = "identify",
  supportingImages = [],
  isUploadingSupportingImages = false,
  supportingImageError = "",
  onSupportingImagesUpload,
  onRemoveSupportingImage,
  isRefinementStep = false,
  requireSupportingPhoto = false,
  supportingPhotoGuidance,
  supportingPhotosLocked = false,
  onSupportingPhotosSignIn,
  photoCap = 3,
  lawns = [],
  lawnChoice,
  lawnChoiceRequired = false,
  lawnChoiceMade = true,
  onLawnChoice,
  photoQuality = null,
  supportingPhotoQuality = {},
  onQualityRetake,
  funnelId = null,
  authState = 'unknown',
  className,
}: SupportingPhotoStepProps) {
  const isDiagnose = mode === "diagnose"
  const showRefinementPanel = isRefinementStep && Boolean(onSupportingImagesUpload)
  // A locked visitor's cap is 1, so the slot math alone would hide the panel's
  // only affordance. Keep exactly one slot visible: it is what the sign-in ask
  // is about.
  const supportingSlotCount = Math.max(supportingPhotosLocked ? 1 : 0, photoCap - 1)
  const canAddSupportingPhoto = supportingImages.length < supportingSlotCount
  const canAnalyze = Boolean(
    !isUploadingSupportingImages &&
    (!lawnChoiceRequired || lawnChoiceMade) &&
    (!requireSupportingPhoto || supportingImages.length > 0),
  )

  const supportingPanel = (
    <div className="mx-auto max-w-md rounded-2xl border border-green-100 bg-green-50/50 p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white text-green-700 ring-1 ring-green-100">
          <Images className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">
            {supportingPhotosLocked
              ? "Add a second view, free account"
              : "Add one more view to refine this result"}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-gray-600">
            {supportingPhotoGuidance || (isDiagnose
              ? "Add a close-up of the damaged blades or the edge between healthy and affected grass."
              : "Add a blade close-up or a wider view of how the grass grows across the lawn.")}
          </p>
        </div>
      </div>

      {/* Recognition over recall: the primary photo leads the strip as filled
          slot 1, so "one done, one to go" reads as images rather than as a
          sentence. The dashed tile is the targeted view we are asking for. */}
      <div className="mt-3 grid grid-cols-3 gap-2">
        <div className="relative overflow-hidden rounded-xl border border-green-100 bg-white">
          <img
            src={uploadedImage}
            alt="Your first lawn photo"
            className="ph-no-capture h-24 w-full object-cover"
            data-ph-no-capture="true"
          />
          <span className="absolute left-1.5 top-1.5 inline-flex items-center rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-green-700">
            1
          </span>
        </div>
        {supportingImages.map((image, index) => {
          const supportingReason = supportingPhotoQuality[image.url]
          const SupportingIcon = supportingReason ? SUPPORTING_QUALITY_ICON[supportingReason] : null
          return (
          <div key={image.url} className="relative overflow-hidden rounded-xl border border-green-100 bg-white">
            <img
              src={image.preview}
              alt={`Supporting lawn view ${index + 1}`}
              className="h-24 w-full object-cover"
            />
            {supportingReason && SupportingIcon && (
              <span
                data-testid={`supporting-quality-${image.url}`}
                className="absolute inset-x-0 bottom-0 inline-flex items-center gap-1 bg-amber-50/95 px-1.5 py-1 text-[10px] font-semibold text-amber-800"
              >
                <SupportingIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                {SUPPORTING_QUALITY_LABEL[supportingReason]}
              </span>
            )}
            <button
              type="button"
              onClick={() => onRemoveSupportingImage?.(image.url)}
              className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-gray-950/75 text-white hover:bg-gray-950 focus:outline-hidden focus:ring-2 focus:ring-white"
              aria-label={`Remove supporting view ${index + 1}`}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
          )
        })}
        {canAddSupportingPhoto && (
          <div className="flex h-24 items-center justify-center rounded-xl border border-dashed border-green-300 bg-white/60 text-green-600">
            {supportingPhotosLocked ? (
              <Lock className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Plus className="h-4 w-4" aria-hidden="true" />
            )}
          </div>
        )}
      </div>

      {canAddSupportingPhoto && (supportingPhotosLocked ? (
        <button
          type="button"
          onClick={onSupportingPhotosSignIn}
          className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-green-200 bg-white px-4 py-2.5 text-sm font-semibold text-green-700 transition-colors hover:bg-green-50 focus:outline-hidden focus:ring-2 focus:ring-green-500/30"
        >
          <Lock className="h-4 w-4" aria-hidden="true" />
          Add a second photo, free account
        </button>
      ) : (
        <label className="mt-3 inline-flex min-h-[44px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-green-200 bg-white px-4 py-2.5 text-sm font-semibold text-green-700 transition-colors hover:bg-green-50 focus-within:ring-2 focus-within:ring-green-500/30">
          {isUploadingSupportingImages ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="h-4 w-4" aria-hidden="true" />
          )}
          {isUploadingSupportingImages ? "Adding photos..." : "Add recommended photo"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            disabled={isUploadingSupportingImages}
            className="sr-only"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? [])
              if (files.length > 0) onSupportingImagesUpload?.(files)
              event.target.value = ""
            }}
          />
        </label>
      ))}
      {supportingImageError && (
        <p role="alert" className="mt-2 text-xs leading-relaxed text-red-600">{supportingImageError}</p>
      )}
      {requireSupportingPhoto && supportingImages.length === 0 && !supportingImageError && (
        <p className="mt-2 text-xs leading-relaxed text-gray-500">
          Your original photo is saved. Add this view before running the refinement.
        </p>
      )}
    </div>
  )

  return (
    <div className={cn("text-left", className)}>
      {onReset && (
        <div className="text-center sm:text-left">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 min-h-[44px] rounded-full border border-gray-200 bg-white text-xs font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            Use a different photo
          </button>
        </div>
      )}

      <div className="flex flex-col items-center mt-4 mb-5 sm:mt-7 sm:mb-7">
        <img
          src={uploadedImage}
          alt="Your grass photo"
          className="ph-no-capture w-40 h-40 sm:w-56 sm:h-56 object-cover rounded-2xl shadow-xs"
          data-ph-no-capture="true"
        />
      </div>

      {photoQuality && onQualityRetake && (
        <div className="mb-6">
          <PhotoQualityWarning
            warning={photoQuality}
            mode={mode}
            funnelId={funnelId}
            authState={authState}
            onRetake={onQualityRetake}
          />
        </div>
      )}

      {showRefinementPanel && <div className="mb-6">{supportingPanel}</div>}

      <div className="max-w-md mx-auto">
        {lawnChoiceRequired && onLawnChoice ? (
          <AnalysisLawnChoice
            lawns={lawns}
            choice={lawnChoiceMade ? lawnChoice : undefined}
            onChange={onLawnChoice}
            mode={mode}
          />
        ) : null}

        <button
          type="button"
          onClick={onAnalyze}
          disabled={!canAnalyze}
          data-testid="analyze-button"
          className={cn(
            "w-full inline-flex items-center justify-center gap-2 py-4 rounded-[10px] text-base font-semibold transition-colors",
            canAnalyze
              ? "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
              : "bg-gray-100 text-gray-400 cursor-not-allowed",
          )}
        >
          {isDiagnose ? "Diagnose problem" : isRefinementStep ? "Refine my result" : "Identify my grass"}
          <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
}
